import React, { useState } from 'react';
import { FaTrash, FaTags } from 'react-icons/fa';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

/**
 * AliasesEditor
 * ─────────────
 * Reusable chip-style editor for a recipe's `match_aliases` field. Used in
 * both AddRecipePage (new recipe) and EditRecipePage (edit existing) so admins
 * and editors can curate the alternative names that the Smart-Search cascade
 * matches on PASS-2 (alias hit).
 *
 * UX rules:
 *   • Enter or "Tilføj" pushes the trimmed value as a new chip.
 *   • Commas inside the input become alias separators (paste-friendly).
 *   • Case-insensitive dedupe so "Frosé" and "frosé" can't both be added.
 *   • Empty / 1-char inputs are rejected with a soft hint.
 *   • A "Foreslå fra navn" affordance is intentionally LEFT OUT — the AI
 *     suggestion mode is a separate, opt-in feature.
 *
 * Props:
 *   value:    string[]               — current aliases array
 *   onChange: (next: string[]) => void
 *   disabled: boolean                — read-only mode (e.g. guest viewing)
 */
const AliasesEditor = ({ value = [], onChange, disabled = false }) => {
  const [input, setInput] = useState('');

  const addAliases = (raw) => {
    if (!raw) return;
    // Allow paste-style "alias1, alias2, alias3" entry
    const candidates = raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length >= 2);
    if (candidates.length === 0) return;
    const existingLower = new Set((value || []).map((v) => v.toLowerCase()));
    const next = [...value];
    for (const c of candidates) {
      const key = c.toLowerCase();
      if (existingLower.has(key)) continue;
      existingLower.add(key);
      next.push(c);
    }
    onChange(next);
    setInput('');
  };

  const handleAdd = () => addAliases(input);

  const removeAlias = (alias) => {
    onChange(value.filter((a) => a !== alias));
  };

  return (
    <div data-testid="recipe-aliases-editor">
      <div className="flex items-center gap-2 mb-1">
        <FaTags className="text-cyan-600" />
        <Label className="m-0">Aliaser / Søgeord</Label>
      </div>
      <p className="text-xs text-gray-500 mb-2">
        Alternative navne så Smart-Search finder opskriften. F.eks. &quot;Frosé&quot;,
        &quot;Frozen Rosé&quot;, &quot;Pina Colada&quot;. Komma adskiller flere på én gang.
      </p>

      {!disabled && (
        <div className="flex gap-2 mb-2">
          <Input
            data-testid="alias-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder='Tilføj alias og tryk Enter…'
          />
          <Button
            type="button"
            data-testid="add-alias-button"
            onClick={handleAdd}
            disabled={input.trim().length < 2}
          >
            Tilføj
          </Button>
        </div>
      )}

      <div
        className="flex flex-wrap gap-2"
        data-testid="alias-chip-list"
        aria-label="Aliases for this recipe"
      >
        {(value || []).length === 0 && (
          <span className="text-xs text-gray-400 italic">
            Ingen aliaser endnu.
          </span>
        )}
        {(value || []).map((alias, index) => (
          <span
            key={`${alias}-${index}`}
            data-testid={`alias-chip-${index}`}
            className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-2"
          >
            {alias}
            {!disabled && (
              <button
                type="button"
                data-testid={`remove-alias-${index}`}
                onClick={() => removeAlias(alias)}
                aria-label={`Remove alias ${alias}`}
                className="text-purple-500 hover:text-purple-800"
              >
                <FaTrash size={10} />
              </button>
            )}
          </span>
        ))}
      </div>
    </div>
  );
};

export default AliasesEditor;
